import type { AuthorizedCard, AuthorizedCards, Card } from '@shared/cards-types';
import { createQuery, request } from '@luna/api';
import { getEnv } from '@luna/env';
import { getFeatureConfig } from '@luna/feature-config';

import { isMainCreditCard } from '../services/typeService';

export interface AuthorizedCardsParams {
    cardId: string;
    shadowAccountId: string;
}

function hasAuthorizedCards(card?: Card): boolean {
    return (
        isMainCreditCard(card) &&
        getFeatureConfig((features) => features.cards.bank.supplementary.enabled)
    );
}

export const getAuthorizedCards = async ({
    cardId,
    shadowAccountId,
}: AuthorizedCardsParams): Promise<AuthorizedCards> => {
    const url = `${
        getEnv().prestoUrl
    }netbanking/my/cards/${cardId}/authorized-cards?shadow-account-id=${shadowAccountId}`;

    return request<AuthorizedCards>(url, {
        headers: {
            'Content-Type': 'application/vnd.presto.latest+json',
            Accept: 'application/vnd.at.spardat.rest.netbanking.model.cards.authorized+json',
        },
    });
};

export const condition = (underlyingCard: Card): boolean => {
    if (!underlyingCard || !hasAuthorizedCards(underlyingCard)) {
        return false;
    }

    const { id: cardId, creditCardShadowAccountId: shadowAccountId } = underlyingCard;

    return !!cardId && !!shadowAccountId;
};

export const [useAuthorizedCards] = createQuery({
    name: 'searchBar/authorizedCards',
    payloadCreator: async (underlyingCard: Card): Promise<AuthorizedCard[]> => {
        if (!underlyingCard) {
            return [];
        }

        const { id: cardId, creditCardShadowAccountId: shadowAccountId = '' } = underlyingCard;

        const authorizedCardsResponse = await getAuthorizedCards({ cardId, shadowAccountId });

        return authorizedCardsResponse.authorizedCards || [];
    },
    cacheKey: (underlyingCard) => (underlyingCard ? underlyingCard.id : ''),
    condition,
});
